import styled from 'styled-components';
import { tokens } from '@cellula/react-theme-patient';
import { Chat } from '@feature/chat/mod';
import { ChatCommunicatorProtocol, ParsedMessage } from '@feature/chat/chat_communicator_protocol';
import { ViewNavBar } from '@component/view_nav_bar.component';
import { Screen } from '@component/screen/screen.component';
import { useEffect, useRef } from 'react';
import { usePresentation } from '@visiba/presentation';
import { useService } from '@visiba/instantiation';
import { Text, VisuallyHidden } from '@cellula/react';
import { DateDisplayFormats, DateService } from '@application/time/date.service';
import { MessagingCaseModel, MessagingData, CaseService } from '@feature/cases/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { BreakPoints, useBreakpoint } from '@cellula/react';
import { AnalyticLink, AnalyticProvider } from '@application/analytic/analytic_link/mod';
import { SocketMessagesApiModel } from '@api_patient/models/SocketMessagesApiModel';
import type { SocketController } from '@visiba/axon';
import type { MessagingMetaData } from '@feature/cases/case_messaging_model';

class VisitsChatCommunicator implements ChatCommunicatorProtocol {
	public readonly analyticCategory = AnalyticCategories.Visits;

	public readonly topics = Object.freeze({
		getHistory: 'getmessages',
		retrieveMessages: 'messages',
		sendMessage: 'sendmessage',
	});

	public readonly fileUploadDispositionUri = 'client/v2/userfiles';

	// Set to null to initialize socket instead.
	public readonly controllerInstance = null;

	constructor(public readonly connectionId: string) {
		// Empty
	}

	isValidSession(response: SocketMessagesApiModel): boolean {
		return this.connectionId === response.caseId.toString();
	}

	getHistory(controllerInstance: SocketController, callback: (data: unknown) => void): void {
		controllerInstance.invoke(
			this.topics.getHistory,
			{
				caseId: this.connectionId,
			},
			(response: SocketMessagesApiModel) => {
				callback(response);
			},
		);
	}

	parseIncomingMessages(response: SocketMessagesApiModel): ParsedMessage[] {
		return response.data;
	}

	parseOutgoingMessage(message: string | null, fileTickets: string[]): Record<string, unknown> {
		return {
			caseId: this.connectionId,
			text: message ?? '',
			fileTickets: fileTickets,
			textFormat: 'Plaintext',
		};
	}
}

interface Props {
	model: MessagingCaseModel;
}

const elements = {
	container: styled.div`
		background-color: ${tokens.color.bg.surface};
		width: 100%;
		height: 100%;
		overflow-x: hidden;
		position: relative;
	`,
	navBarWrapper: styled.div`
		position: fixed;
		top: 0;
		width: 100%;
		z-index: 1;

		${tokens.breakpointQuery.medium} {
			position: static;
		}
	`,
	hiddenNavBarGoBackLink: styled(AnalyticLink)`
		position: absolute;
		top: ${tokens.spacing.x1};
		left: ${tokens.spacing.x1};
		opacity: 0;
		z-index: -999;

		&:focus {
			opacity: 1;
			z-index: 0;
		}
	`,
};

export function InfoViewMessaging({ model }: Props): JSX.Element {
	const shouldUseScreen = useBreakpoint(BreakPoints.Small);

	return (
		<AnalyticProvider category={AnalyticCategories.VisitsChat}>
			<Screen route='infoViewMessaging' canBeActivated={shouldUseScreen} isActivated>
				<Inner model={model} />
			</Screen>
		</AnalyticProvider>
	);
}

function Inner({ model }: Props): JSX.Element {
	const data = model.get();

	const chatCommunicatorRef = useRef<VisitsChatCommunicator>();
	if (chatCommunicatorRef.current == null || chatCommunicatorRef.current.connectionId !== data.id) {
		chatCommunicatorRef.current = new VisitsChatCommunicator(data.id);
	}

	const ref = useRef<HTMLDivElement>(null);

	const presentation = usePresentation(model.presentation);
	const caseService = useService(CaseService);
	const dateService = useService(DateService);
	const analyticService = useService(AnalyticService);

	useEffect(
		function analyticQuirk_whenComponentMounts() {
			analyticService.trackPageView({ customTitle: 'My Cases-Messaging' });
		},
		[analyticService],
	);

	useEffect(
		function whenComponentMounts() {
			model.analyticQuirk_trackOnMountInVisitsView();
			model.updateExtendedData();
		},
		[model],
	);

	function handleMarkAsCompleted(): void {
		model.markAsCompleted(dateService.newDate());
	}

	function handleReceivedHistory(): void {
		model.markAsRead();
		caseService.updateSync();
	}

	return (
		<elements.container role='region' aria-labelledby='infoViewMessageTitle' ref={ref}>
			<VisuallyHidden key={data.id /* Key here to get React to invalidate the v-dom diff. Forcing a rerender of this component */}>
				<h1 id='infoViewMessageTitle'>{withTranslation('PW.Component.Visits.MessagingInfo.A11y.Region')}</h1>
			</VisuallyHidden>
			<Chat
				communicator={chatCommunicatorRef.current}
				issuesTrackedById={MessagingCaseModel.MESSAGE_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + data.id}
				onMarkAsCompleted={handleMarkAsCompleted}
				onReceivedHistory={handleReceivedHistory}
				isReadyToJoin={presentation.metaData != null}
				connectedId={data.id}
				forceHideActions={data.completed || !presentation.metaData?.allowClientResponse || !!presentation.metaData.completedTimestamp}
				hasReadUntilData={presentation.metaData?.hasReadUntilTimestamp}
				header={<NavBar data={data} metaData={presentation.metaData} />}
				showTopBanner={
					<Text variant='labelXSmallRegular' style={{ color: tokens.color.content.muted }}>
						{withTranslation('PW.Component.Visits.MessagingInfo.CaseStarted', {
							dateTime: data.date.format(
								DateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY),
							),
						})}
					</Text>
				}
				showBottomBanner={
					presentation.metaData?.completedTimestamp != null ? (
						<Text variant='labelXSmallRegular' style={{ color: tokens.color.content.muted }}>
							{withTranslation('PW.Component.Visits.MessagingInfo.CaseCompleted', {
								dateTime: presentation.metaData.completedTimestamp.format(
									dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY),
								),
							})}
						</Text>
					) : !presentation.metaData?.allowClientResponse ? (
						<Text variant='labelXSmallRegular' style={{ color: tokens.color.content.muted }}>
							{withTranslation('PW.Component.Visits.MessagingInfo.ResponseNotAllowed')}
						</Text>
					) : null
				}
			/>
		</elements.container>
	);
}

interface NavBarProps {
	data: MessagingData;
	metaData: MessagingMetaData | null;
}
function NavBar({ data, metaData }: NavBarProps): JSX.Element {
	const desktop = useBreakpoint(BreakPoints.Medium);
	const analyticAction = 'Clicked go to My cases-back arrow';

	const receiptUrl = metaData?.receiptUrl;
	const actions = receiptUrl
		? [{ label: withTranslation('PW.Component.Visits.MessagingInfo.ShowReceipt'), onSelect: () => window.open(receiptUrl, '_blank') }]
		: undefined;

	return (
		<elements.navBarWrapper>
			{desktop ? (
				<elements.hiddenNavBarGoBackLink action={analyticAction} to='../../' replace>
					{withTranslation('PW.Component.Visits.MessagingInfo.A11y.GoBack')}
				</elements.hiddenNavBarGoBackLink>
			) : null}
			<ViewNavBar analyticAction={analyticAction} to={!desktop ? '../../' : undefined} title={data.receptionName} actions={actions} />
		</elements.navBarWrapper>
	);
}
